// @greenlight-spec: 11-observability.md#app-usage-collection
// @greenlight-spec: 16-api-conventions.md
import { z } from 'zod';
const isoDateTime = () => z.string().datetime({ offset: true });
const isoDate = () => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
// Usage reads aggregate over UTC days. The window is bounded by the
// app_usage_presence retention (60 days), which caps how far back the
// distinct-user totals can be computed exactly; daily rollups themselves are
// retained longer (13 months) for future trend surfaces.
export const APP_USAGE_MAX_WINDOW_DAYS = 60;
export const APP_USAGE_DEFAULT_WINDOW_DAYS = 30;
export const AppUsageWindowQuerySchema = z.object({
    window_days: z.coerce
        .number()
        .int()
        .min(1)
        .max(APP_USAGE_MAX_WINDOW_DAYS)
        .default(APP_USAGE_DEFAULT_WINDOW_DAYS),
});
// One rolled-up UTC day of authenticated app traffic. The series is sparse:
// days with no authenticated requests have no point, never a zero-filled one.
export const AppUsageDayPointSchema = z.object({
    day: isoDate(),
    request_count: z.number().int().nonnegative(),
    active_user_count: z.number().int().nonnegative(),
});
// Per-app usage over a trailing window. `totals.active_users` is the exact
// distinct-user count across the whole window (not a sum of daily counts);
// `last_active_at` is the most recent authenticated access ever recorded for
// the app, independent of the requested window. Aggregates only — no
// per-user activity is ever exposed.
export const AppUsageSchema = z.object({
    app_id: z.string().uuid(),
    window_days: z.number().int().positive(),
    from: isoDate(),
    to: isoDate(),
    series: z.array(AppUsageDayPointSchema),
    totals: z.object({
        request_count: z.number().int().nonnegative(),
        active_users: z.number().int().nonnegative(),
    }),
    last_active_at: isoDateTime().nullable(),
});
// Fleet-level per-app aggregate over one shared window — the traffic-weighting
// input for the master system map. Covers every live (non-deleted) app in the
// org, zero-valued when unused, so consumers can join by app_id without
// special-casing absent rows.
export const AppUsageSummaryItemSchema = z.object({
    app_id: z.string().uuid(),
    request_count: z.number().int().nonnegative(),
    active_users: z.number().int().nonnegative(),
    last_active_at: isoDateTime().nullable(),
});
export const AppUsageSummaryResultSchema = z.object({
    window_days: z.number().int().positive(),
    from: isoDate(),
    to: isoDate(),
    items: z.array(AppUsageSummaryItemSchema),
    /** Distinct people across every live app in the window, each counted once. */
    active_users_total: z.number().int().nonnegative(),
    /** Integration and database calls the proxy brokered for the org's apps in the window. */
    data_calls_total: z.number().int().nonnegative(),
});
//# sourceMappingURL=usage.js.map